import './bootstrap';
import * as Sentry from "@sentry/vue";
import piniaPluginPersistedState from "pinia-plugin-persistedstate"
import { createApp, markRaw, h } from 'vue'
import { createInertiaApp, router } from '@inertiajs/vue3'
import { createPinia } from 'pinia'
import { useAuthStore } from '@stores/AuthStore.js'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import { isThirdPartyFetchNoise, scrubBreadcrumb, scrubEvent } from './sentry/scrub.js'

// setTimeout clamps to a 32-bit signed int; a farther expiry is armed at the cap
// and re-checked on the next navigation/load.
const MAX_TIMEOUT_MS = 2147483647

const PRELOAD_RELOAD_KEY = 'vite-preload-reloaded-at'
const PRELOAD_RELOAD_WINDOW_MS = 30000

// A tab opened before a deploy requests chunks that no longer exist; reload once to get the new build.
if (typeof window !== 'undefined') {
  // Inertia only updates the URL once the page chunk loads, so remember where the visit was going.
  const pendingVisit = { url: null }

  router.on('start', (event) => {
    const { method, url } = event.detail.visit
    pendingVisit.url = method === 'get' ? url?.href ?? null : null
  })
  router.on('finish', () => { pendingVisit.url = null })

  window.addEventListener('vite:preloadError', (event) => {
    try {
      const lastReloadAt = Number(window.sessionStorage.getItem(PRELOAD_RELOAD_KEY))

      if (Date.now() - lastReloadAt < PRELOAD_RELOAD_WINDOW_MS) return

      window.sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()))
    } catch {
      return
    }

    event.preventDefault()

    if (pendingVisit.url) {
      window.location.assign(pendingVisit.url)
    } else {
      window.location.reload()
    }
  })
}

const pinia = createPinia()
pinia.use(piniaPluginPersistedState)

import.meta.glob([
  '../img/**',
])

createInertiaApp({
  // A handled preload error resolves undefined; stay pending while the page reloads.
  resolve: (name) => resolvePageComponent(
    `./pages/${name}.vue`,
    import.meta.glob('./pages/**/*.vue')
  ).then((module) => module ?? new Promise(() => {})),
  setup({ el, App, props, plugin }) {
    const app = createApp({ render: () => h(App, props) })
      .use(plugin)
      .use(pinia)

    const sentryDsn = import.meta.env.VITE_SENTRY_DSN

    if (sentryDsn) {
      Sentry.init({
        app,
        dsn: sentryDsn,
        integrations: [
          Sentry.browserTracingIntegration(),
          // Mask all text/inputs and media in Session Replay; `mask` wires the
          // explicit .sentry-mask selector (e.g. the TFN field). Network request
          // header/body capture is left OFF (the default) so Bearer tokens are
          // never recorded.
          Sentry.replayIntegration({
            maskAllText: true,
            maskAllInputs: true,
            blockAllMedia: true,
            mask: ['.sentry-mask'],
          }),
        ],
        environment: import.meta.env.VITE_APP_ENV,
        // Never attach the browser's cookies/headers/IP as default PII.
        sendDefaultPii: false,
        // Strip the Bearer token, auth headers/cookies and the magic-link ?token
        // from every event and breadcrumb before it leaves the browser.
        beforeSend: (event) => (isThirdPartyFetchNoise(event) ? null : scrubEvent(event)),
        beforeBreadcrumb: (breadcrumb) => scrubBreadcrumb(breadcrumb),
        // Drop noise we cannot action and third-party script errors.
        denyUrls: [
          /googletagmanager\.com/i,
          /google-analytics\.com/i,
          /doubleclick\.net/i,
          /tawk\.to/i,
          /js\.stripe\.com/i,
          /extensions\//i,
          /^chrome-extension:\/\//i,
          /^moz-extension:\/\//i,
        ],
        // Performance Monitoring
        tracesSampleRate: 0.2,
        // Only propagate distributed-tracing headers to the finder API origin, so
        // the Bearer-bearing request never carries the sentry-trace header to a
        // third party.
        tracePropagationTargets: [import.meta.env.VITE_API_URL].filter(Boolean),
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      });
    }

    app.mount(el)

    // --- Token TTL enforcement -------------------------------------------
    // The token+expiry are the only persisted auth state now. Enforce the TTL
    // once on load, repopulate the (no-longer-persisted) client PII for the
    // account menu, and arm a one-shot logout at the exact expiry.
    const authStore = useAuthStore()

    authStore.enforceExpiry()

    if (authStore.isAuthenticated && !authStore.client) {
      // Repopulate the (no-longer-persisted) client identity for the account
      // menu. /auth/check only pings token validity (204, no body); the profile
      // endpoint carries the name, and its 401/403 still drives the interceptor
      // logout just the same. Kept in memory only — never re-persisted.
      window.axios.get('/api/client/profile', {
        headers: { Authorization: `Bearer ${authStore.token}` },
      }).then((response) => {
        const p = response?.data?.data ?? response?.data
        if (p) {
          const name = [p.first_name, p.last_name].filter(Boolean).join(' ').trim()
          authStore.client = { name, email: p.email }
        }
      }).catch(() => {}) // 401/403 handled by the global interceptor (logout)
    }

    if (authStore.isAuthenticated && authStore.expiresAt) {
      const msUntilExpiry = new Date(authStore.expiresAt).getTime() - Date.now()
      const delay = Math.min(Math.max(msUntilExpiry, 0), MAX_TIMEOUT_MS)

      window.setTimeout(() => useAuthStore().enforceExpiry(), delay)
    }

    // Re-validate the client token against the backend on each navigation
    // (debounced ~1/min). The axios interceptor performs a full logout if the
    // token was revoked/expired server-side, so the "signed in" UI never
    // lingers stale across the site.
    let lastValidated = 0
    const validateSession = () => {
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated) return

      const now = Date.now()
      if (now - lastValidated < 60000) return
      lastValidated = now

      window.axios.get('/api/client/auth/check', {
        headers: { Authorization: `Bearer ${authStore.token}` },
      }).catch(() => {}) // 401/403 is handled by the global interceptor (logout)
    }

    validateSession()
    router.on('navigate', validateSession)
  },
})

//const app = createApp(App)
//app.mount('#app')
